import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';
import fr from '@angular/common/locales/fr';
import it from '@angular/common/locales/it';
import ja from '@angular/common/locales/ja';
import nl from '@angular/common/locales/nl';
import pt from '@angular/common/locales/pt';
import zh from '@angular/common/locales/zh';
import { NgModule, inject, provideAppInitializer } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { TwcI18nModule, TwcIconApi, TwcModule } from '@topcon/twc-angular';

import {
    AclConfig,
    AclControlService,
    EntryPointConfig,
    EntryPointService,
    HAS_TO_SHOW_NOTIFICATIONS_TOKEN,
    ITEMS_TOOLBAR_CONFIGURATION_TOKEN,
    MagnetService,
    UI_CONFIGURATION_TOKEN,
    UiConfigurationService,
} from '@mirage-projects/cw-commons';
import { CONFIG_ENVIRONMENT, CORE_CONFIGURATION_TOKEN } from '@mirage-projects/cw-models';
import { FullScreenLoaderModule } from '@mirage-projects/cw-ui-components';
import { GeneralExternalEventsService } from '@mirage-projects/cw-utils';

import CoreConfigurationJson from '@collageweb/assets/configuration/core-configuration.json';
import UiConfigurationJson from '@collageweb/assets/configuration/ui-configuration.json';
import { AuthTokenInterceptor } from '@collageweb/core/interceptors/auth-token.interceptor';
import { ResponseInterceptor } from '@collageweb/core/interceptors/response.interceptor';
import { environment } from '@collageweb/environments/environment';

import { MainNotificationDialogModule } from './components/main-notification-dialog/main-notification-dialog.module';
import { NotificationDrawerModule } from './components/notification-drawer/notification-drawer.module';
import { TopbarModule } from './components/topbar/topbar.module';
import { JsonVersionInterceptor } from './core/interceptors/json-version.interceptor';
import { CWEnvironmentConfiguration } from './core/services/environment-configuration';
import { ToolsConfigurationService } from './core/services/tools-configuration.service';
import { customIcons } from './custom-icons';
import { PageNotFoundComponent } from './pages/pageNotFound/pageNotFound.component';
import { AppServicesModule } from './services/app-services.module';

export function initEntryPoints(
    entryPointService: EntryPointService,
    magnetService: MagnetService
): () => Observable<EntryPointConfig[] | undefined> {
    const isSite = magnetService.isSite();
    return (): Observable<EntryPointConfig[] | undefined> => entryPointService.initEntryPoints(isSite);
}

export function initAclConfig(aclControlService: AclControlService) {
    return (): Observable<AclConfig[]> => aclControlService.initAclConfig();
}

export function initUiConfigurationService(uiConfigurationService: UiConfigurationService) {
    return (): Observable<void> => uiConfigurationService.init();
}

export function initGeneralExternalEvents(generalExternalEventsService: GeneralExternalEventsService) {
    return (): Observable<void> => generalExternalEventsService.init();
}

// Register locales
registerLocaleData(en);
registerLocaleData(es);
registerLocaleData(ja);
registerLocaleData(zh);
registerLocaleData(it);
registerLocaleData(de);
registerLocaleData(fr);
registerLocaleData(pt);
registerLocaleData(nl);

@NgModule({
    declarations: [PageNotFoundComponent],
    exports: [
        CommonModule,
        // Routing
        TwcI18nModule,
        TwcModule,
        TopbarModule,
        NotificationDrawerModule,
        FullScreenLoaderModule,
        AppServicesModule,
    ],
    imports: [
        CommonModule,
        TranslateModule.forChild(),
        // Routing
        TwcModule,
        TopbarModule,
        NotificationDrawerModule,
        FullScreenLoaderModule,
        AppServicesModule,
        MainNotificationDialogModule,
    ],
    providers: [
        DatePipe,
        { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: JsonVersionInterceptor, multi: true },
        { provide: CORE_CONFIGURATION_TOKEN, useValue: CoreConfigurationJson },
        { provide: CONFIG_ENVIRONMENT, useFactory: (): CWEnvironmentConfiguration => environment.configuration },
        { provide: UI_CONFIGURATION_TOKEN, useValue: UiConfigurationJson },
        { provide: ITEMS_TOOLBAR_CONFIGURATION_TOKEN, useValue: ToolsConfigurationService.toolbarConfig },
        { provide: 'locationObject', useValue: location },
        provideAppInitializer(() => {
            const initializerFn = initAclConfig(inject(AclControlService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initEntryPoints(inject(EntryPointService), inject(MagnetService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initUiConfigurationService(inject(UiConfigurationService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = initGeneralExternalEvents(inject(GeneralExternalEventsService));
            return initializerFn();
        }),
        { provide: HAS_TO_SHOW_NOTIFICATIONS_TOKEN, useValue: true },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class SharedModule {
    constructor() {
        TwcIconApi.add(customIcons);
    }
}
